import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Btn, Input, Textarea, Badge, Spinner, Empty } from "../ui";
import { usePermissions } from "../AuthContext";
import {
  Users, X, StickyNote, Activity, CheckCircle2, XCircle, Search, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

const STAGES = ["nou", "contactat", "calificat", "client", "pierdut"];
const STAGE_COLOR = { nou: "slate", contactat: "blue", calificat: "amber", client: "green", pierdut: "red" };
const KIND_LABEL = { apel: "Apel", email: "Email", intalnire: "Intalnire", sarcina: "Sarcina", nota: "Nota" };

function fmt(dt) {
  if (!dt) return "-";
  try { return new Date(dt).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }); }
  catch { return dt; }
}
function fmtDate(dt) {
  if (!dt) return "-";
  try { return new Date(dt).toLocaleDateString("ro-RO", { dateStyle: "short" }); }
  catch { return dt; }
}

function Stat({ label, value, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
    </Card>
  );
}

function DetailDrawer({ tenantId, onClose, onChanged }) {
  const { can } = usePermissions();
  const canEdit = can("crm", "edit");
  const canDelete = can("crm", "delete");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [actKind, setActKind] = useState("apel");
  const [actText, setActText] = useState("");
  const [actDue, setActDue] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/crm/companies/${tenantId}`); setData(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const setStage = async (stage) => {
    try { await api.patch(`/crm/companies/${tenantId}/stage`, { stage }); toast.success("Stadiu actualizat"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/crm/companies/${tenantId}/notes`, { text: noteText }); setNoteText(""); toast.success("Notita adaugata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delNote = async (id) => {
    try { await api.delete(`/crm/companies/${tenantId}/notes/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addAct = async () => {
    if (!actText.trim()) return;
    try { await api.post(`/crm/companies/${tenantId}/activities`, { kind: actKind, text: actText, due_at: actDue || null }); setActText(""); setActDue(""); toast.success("Activitate adaugata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const c = data?.company;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-xl h-full bg-white overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="crm-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 truncate">{c?.name || "Firma"}</h3>
          <button onClick={onClose} data-testid="crm-drawer-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {loading || !c ? <div className="p-8"><Spinner /></div> : (
          <div className="p-5 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-400">Email</span><div className="font-medium break-all">{c.email ? <a href={`mailto:${c.email}`} data-testid="crm-drawer-email" className="text-emerald-600 hover:underline">{c.email}</a> : "-"}</div></div>
              <div><span className="text-slate-400">Telefon</span><div className="font-medium">{c.phone ? <a href={`tel:${c.phone}`} data-testid="crm-drawer-phone" className="text-emerald-600 hover:underline">{c.phone}</a> : "-"}</div></div>
              <div><span className="text-slate-400">WebPropriu</span><div className="font-medium break-all">{c.website ? <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" data-testid="crm-drawer-web" className="text-emerald-600 hover:underline">{c.website}</a> : "-"}</div></div>
              <div><span className="text-slate-400">Site</span><div className="font-medium">{c.has_page && c.slug ? <a href={`${BASE}/site/${c.slug}`} target="_blank" rel="noreferrer" data-testid="crm-drawer-site" className="text-emerald-600 hover:underline">Vezi site</a> : "-"}</div></div>
              <div><span className="text-slate-400">Tara</span><div className="font-medium">{c.country || "-"}</div></div>
              <div><span className="text-slate-400">Judet</span><div className="font-medium">{c.county || "-"}</div></div>
              <div><span className="text-slate-400">Localitate</span><div className="font-medium">{c.locality || "-"}</div></div>
              <div><span className="text-slate-400">Slug</span><div className="font-medium">{c.slug}</div></div>
              <div><span className="text-slate-400">Data intrare</span><div className="font-medium">{fmtDate(c.created_at)}</div></div>
              <div><span className="text-slate-400">Sursa</span><div className="font-medium">{c.source === "outreach_campaign" ? "Campanie" : "Signup"}</div></div>
              <div><span className="text-slate-400">Pagina</span><div className="font-medium">{c.has_page ? "Da" : "Nu"}</div></div>
              <div><span className="text-slate-400">Proforme</span><div className="font-medium">{c.proforme}</div></div>
              <div><span className="text-slate-400">Plata</span><div className="font-medium">{c.paid ? `Da (${c.paid_eur} EUR)` : "Nu"}</div></div>
              <div><span className="text-slate-400">Cont</span><div className="font-medium capitalize">{c.status}</div></div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Stadiu vanzare</div>
              <div className="flex flex-wrap gap-2">
                {STAGES.map((s) => (
                  <button key={s} onClick={() => setStage(s)} data-testid={`crm-stage-${s}`} disabled={!canEdit}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${c.stage === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-slate-700 font-semibold mb-2"><Activity className="w-4 h-4" /> Activitati</div>
              {canEdit && (
              <div className="rounded-lg border border-slate-200 p-3 mb-3 space-y-2">
                <div className="flex flex-col sm:flex-row gap-2">
                  <select value={actKind} onChange={(e) => setActKind(e.target.value)} data-testid="crm-activity-kind"
                    className="px-2 py-2 text-sm rounded-lg border border-slate-200 outline-none focus:border-emerald-500 sm:w-32">
                    {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <div className="flex-1">
                    <label className="block text-xs text-slate-400 mb-1">Programare (data si ora)</label>
                    <Input type="datetime-local" value={actDue} onChange={(e) => setActDue(e.target.value)} data-testid="crm-activity-due" />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Textarea value={actText} onChange={(e) => setActText(e.target.value)} placeholder="Descriere activitate" data-testid="crm-activity-text" />
                  <Btn onClick={addAct} data-testid="crm-activity-add" className="w-full sm:w-auto sm:self-end">Adauga</Btn>
                </div>
              </div>
              )}
              <div className="space-y-2 max-h-64 overflow-auto" data-testid="crm-activities-list">
                {(data.activities || []).length === 0 ? <p className="text-sm text-slate-400">Nicio activitate.</p> :
                  data.activities.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${a.manual ? "bg-emerald-500" : "bg-slate-300"}`} />
                      <div className="flex-1">
                        <div className="text-slate-800">
                          {a.manual && <span className="font-medium mr-1">{KIND_LABEL[a.kind] || a.kind}:</span>}
                          {a.label}
                          {a.due_at && <span className="text-amber-600"> (programat {fmt(a.due_at)})</span>}
                        </div>
                        <div className="text-xs text-slate-400">{fmt(a.at)}</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-slate-700 font-semibold mb-2"><StickyNote className="w-4 h-4" /> Notite</div>
              {canEdit && (
              <div className="flex flex-col gap-2 mb-3">
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Scrie o notita" data-testid="crm-note-text" />
                <Btn onClick={addNote} data-testid="crm-note-add" className="w-full sm:w-auto sm:self-end">Adauga</Btn>
              </div>
              )}
              <div className="space-y-2" data-testid="crm-notes-list">
                {(data.notes || []).length === 0 ? <p className="text-sm text-slate-400">Nicio notita.</p> :
                  data.notes.map((n) => (
                    <div key={n.id} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-slate-800 whitespace-pre-wrap flex-1">{n.text}</p>
                        <button onClick={() => delNote(n.id)} data-testid={`crm-note-del-${n.id}`} style={{ display: canDelete ? undefined : "none" }}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></button>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">{n.author} · {fmt(n.created_at)}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Crm() {
  const [ov, setOv] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([api.get("/crm/overview"), api.get("/crm/companies")]);
      setOv(o.data); setItems(c.data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((r) => {
    const okQ = !q || (r.name || "").toLowerCase().includes(q.toLowerCase()) || (r.email || "").toLowerCase().includes(q.toLowerCase());
    const okS = !stageFilter || r.stage === stageFilter;
    return okQ && okS;
  });

  return (
    <div>
      <PageHeader title="CRM" subtitle="Firmele cu cont pe Yvola. Doar super admin." />

      {loading ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Stat label="Trimise" value={ov?.sent ?? 0} testid="crm-stat-sent" />
            <Stat label="Accesate" value={ov?.accessed ?? 0} testid="crm-stat-accessed" />
            <Stat label="Activate" value={ov?.activated ?? 0} testid="crm-stat-activated" />
            <Stat label="Firme cont" value={ov?.tenants_total ?? 0} testid="crm-stat-tenants" />
            <Stat label="Conversie" value={`${ov?.conversion_rate ?? 0}%`} testid="crm-stat-conversion" />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cauta dupa nume sau email" className="pl-9" data-testid="crm-search" />
            </div>
            <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="crm-stage-filter"
              className="px-3 py-2 text-sm rounded-lg border border-slate-200 outline-none focus:border-emerald-500">
              <option value="">Toate stadiile</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {filtered.length === 0 ? (
            <Card><Empty icon={Users} title="Nicio firma" hint="Nu exista firme cu cont." /></Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap" data-testid="crm-table">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-200">
                      <th className="px-4 py-3 font-medium">Firma</th>
                      <th className="px-4 py-3 font-medium">Telefon</th>
                      <th className="px-4 py-3 font-medium">WebPropriu</th>
                      <th className="px-4 py-3 font-medium">Site</th>
                      <th className="px-4 py-3 font-medium">Tara</th>
                      <th className="px-4 py-3 font-medium">Judet</th>
                      <th className="px-4 py-3 font-medium">Localitate</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Intrare</th>
                      <th className="px-4 py-3 font-medium">Sursa</th>
                      <th className="px-4 py-3 font-medium text-center">Pagina</th>
                      <th className="px-4 py-3 font-medium text-center">Proforme</th>
                      <th className="px-4 py-3 font-medium">Plata</th>
                      <th className="px-4 py-3 font-medium">Ultima activ.</th>
                      <th className="px-4 py-3 font-medium">Stadiu</th>
                      <th className="px-4 py-3 font-medium">Cont</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.tenant_id} onClick={() => setSel(r.tenant_id)} data-testid={`crm-row-${r.tenant_id}`}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{r.name}</div>
                          <div className="text-xs text-slate-400">{r.slug}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {r.phone
                            ? <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} data-testid={`crm-phone-${r.tenant_id}`} className="text-emerald-600 hover:underline">{r.phone}</a>
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {r.website
                            ? <a href={r.website.startsWith("http") ? r.website : `https://${r.website}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`crm-web-${r.tenant_id}`} className="text-emerald-600 hover:underline">{r.website}</a>
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {r.has_page && r.slug
                            ? <a href={`${BASE}/site/${r.slug}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`crm-site-${r.tenant_id}`} className="text-emerald-600 hover:underline">Vezi site</a>
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{r.country || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">{r.county || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">{r.locality || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {r.email
                            ? <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} data-testid={`crm-email-${r.tenant_id}`} className="text-emerald-600 hover:underline">{r.email}</a>
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3 text-slate-600">{r.source === "outreach_campaign" ? "Campanie" : "Signup"}</td>
                        <td className="px-4 py-3 text-center">{r.has_page ? <CheckCircle2 className="w-4 h-4 text-emerald-500 inline" /> : <XCircle className="w-4 h-4 text-slate-300 inline" />}</td>
                        <td className="px-4 py-3 text-center text-slate-700">{r.proforme}</td>
                        <td className="px-4 py-3 text-slate-600">{r.paid ? `${r.paid_eur} EUR` : "Nu"}</td>
                        <td className="px-4 py-3 text-slate-600">{fmtDate(r.last_activity)}</td>
                        <td className="px-4 py-3"><Badge color={STAGE_COLOR[r.stage] || "slate"}>{r.stage}</Badge></td>
                        <td className="px-4 py-3"><Badge color={r.status === "active" ? "green" : "red"}>{r.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {sel && <DetailDrawer tenantId={sel} onClose={() => setSel(null)} onChanged={load} />}
    </div>
  );
}
